// The changelist server's typed errors (`{"error": code, "detail"?}`, see
// crates/server/src/error.rs), in plain words with what the user can do.
// The one place a code is worded: failed requests carry this text as their
// message (http.ts), and failed loads show it (failure.ts).
//
// `upstream_error` and `upstream_timeout` (GitHub failing) are worded here
// for writes only (`githubWriteWords`); a failed load words them by its
// cause (failure.ts). Codes worded elsewhere are not here: `rate_limited`
// (its wait), `unauthorized`, `reauth_required` and `account_changed`
// (sign-in), `not_found` (no access) and `internal` (a server error).

export interface ServerErrorWords {
  /** What happened, as a sentence. */
  text: string;
  /** What the user can do about it; null when there is nothing to add. */
  advice: string | null;
}

const ending = (s: string) => (/[.!?]$/.test(s) ? s : `${s}.`);
const sentence = (s: string) => ending(s.charAt(0).toUpperCase() + s.slice(1));
/** ": detail." when there is a detail, else ".". */
const withDetail = (s: string, detail: string | null) => (detail ? ending(`${s}: ${detail}`) : `${s}.`);

const WORDS: Record<string, (detail: string | null) => ServerErrorWords> = {
  policy_pending: () => ({
    text: "Changeling hasn't resolved this organization's policy yet, so it can't apply it.",
    advice: 'An organization admin can open its Changeling settings and save the policy; then try again.',
  }),
  app_key_missing: () => ({
    text: "Changeling's GitHub App has no private key on the server.",
    advice: 'Whoever runs Changeling needs to register the App again or set its private key.',
  }),
  not_configured: () => ({
    text: "Changeling isn't set up yet: no GitHub App is configured.",
    advice: 'Whoever runs Changeling can finish setting it up at /setup.',
  }),
  installations_pending: () => ({
    text: 'Changeling is still learning where its GitHub App is installed.',
    advice: 'This takes a few seconds after the server starts; try again shortly.',
  }),
  not_installed: () => ({
    text: "The Changeling GitHub App isn't installed on this repository.",
    advice: 'Ask an owner of the repository to install it.',
  }),
  bridge_denied: (d) => ({ text: withDetail("Changeling's policy doesn't allow this", d), advice: null }),
  forbidden: (d) => ({ text: withDetail("Changeling doesn't allow this for your account", d), advice: null }),
  csrf_required: () => ({
    text: "Changeling refused the request because it didn't come from its own page.",
    advice: 'Reload the page and try again.',
  }),
  bad_request: (d) => ({
    text: withDetail("Changeling's server couldn't accept the request", d),
    advice: 'Reload the page; if it keeps happening, send the details to whoever runs changelist.',
  }),
  too_many: (d) => ({ text: d ? sentence(d) : 'There are too many of these already.', advice: null }),
  conflict: (d) => ({
    text: d ? sentence(d) : 'Something changed meanwhile.',
    advice: 'Reload to see the latest, then try again.',
  }),
  publish_running: () => ({
    text: 'A publish of this review is still running.',
    advice: 'Wait for it to finish, then try again.',
  }),
  request_timeout: () => ({
    text: "The request didn't arrive at Changeling's server in time.",
    advice: 'Check your connection, then try again.',
  }),
  payload_too_large: () => ({
    text: 'The request was too large for Changeling’s server.',
    advice: 'Make it smaller, for example a shorter comment, and try again.',
  }),
};

/** The words for a changelist error response's body; null when it is not a typed error worded here. */
export function serverErrorWords(body: unknown): ServerErrorWords | null {
  if (typeof body !== 'object' || body === null) return null;
  const { error, detail } = body as { error?: unknown; detail?: unknown };
  if (typeof error !== 'string' || !Object.hasOwn(WORDS, error)) return null;
  return WORDS[error](typeof detail === 'string' && detail.trim() ? detail.trim() : null);
}

/**
 * The words for GitHub failing a write (`upstream_timeout`, `upstream_error`);
 * null for any other body. A write GitHub didn't answer in time may still
 * have happened.
 */
export function githubWriteWords(body: unknown): ServerErrorWords | null {
  if (typeof body !== 'object' || body === null) return null;
  const { error, detail } = body as { error?: unknown; detail?: unknown };
  if (error === 'upstream_timeout')
    return {
      text: "GitHub didn't answer in time, so this may or may not have gone through.",
      advice: 'Check before trying again.',
    };
  if (error === 'upstream_error')
    return {
      text: `GitHub couldn't complete this just now${typeof detail === 'string' && detail ? ` (${detail})` : ''}.`,
      advice: 'Try again in a moment.',
    };
  return null;
}
